import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from '@tanstack/react-form';
import { z } from 'zod';

import { Form } from '@components/Form';
import { useFormContext } from '@components/Form/FormProvider';
import { Field, FieldLabel, FieldContent, FieldError } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { getFieldErrors } from '@api/client';
import type { ApiContact, ContactBillingAddress } from '@api/contacts';
import { useContactBillingAddresses } from '@hooks/contacts';
import { useTranslation } from '@hooks/useTranslation';
import { BILLING_POSTAL_CODE_PATTERN, isBillingPostalCodeValid } from '@/react/utils/postalCode';
import { SubmitButton } from './SubmitButton';

import { FIELD_SURFACE_BORDERED } from "@/components/ui/field-surface";
import { cn } from "@/lib/utils";
const LABEL_CLASS = 'font-inter text-sm font-semibold text-grey leading-normal';
const INPUT_CLASS =
  cn(FIELD_SURFACE_BORDERED, 'font-inter h-[45px] w-full px-6 py-4 text-sm text-off-black placeholder:text-grey-light focus-visible:outline-none focus-visible:ring-0');
const FIELD_NAMES = ['name', 'address', 'postal_code'] as const;
const NAME_FIELD_ID = 'create-billing-address-name';
const ADDRESS_FIELD_ID = 'create-billing-address-address';
const POSTAL_CODE_FIELD_ID = 'create-billing-address-postal-code';

function createBillingAddressFormSchema(t: (key: string) => string) {
  return z.object({
    // Optional billing-name label (GRAIN-2568): blank falls back to org/contact.
    name: z.string().trim().optional().default(''),
    address: z.string().trim().min(1, t('create_billing_address.validation.address_required')),
    postal_code: z
      .string()
      .trim()
      .min(1, t('create_billing_address.validation.postal_code_required'))
      .regex(BILLING_POSTAL_CODE_PATTERN, t('create_billing_address.validation.postal_code_invalid')),
  });
}

type CreateBillingAddressFormData = z.infer<ReturnType<typeof createBillingAddressFormSchema>>;

interface CreateBillingAddressFormProps {
  contactId: number;
  onSuccess: (contact: ApiContact, billingAddress: ContactBillingAddress) => void;
  onPendingChange?: (isPending: boolean) => void;
}

function useServerErrors(error: Error | null) {
  const { form } = useFormContext();

  useEffect(() => {
    const errors = getFieldErrors(error) ?? {};
    FIELD_NAMES.forEach((name) => {
      const message = errors[name];
      form.setFieldMeta(name, (prev) => ({
        ...prev,
        errorMap: { ...prev.errorMap, onSubmit: message ? { message } : undefined },
      }));
    });
  }, [error, form]);
}

function findCreatedBillingAddress(
  contact: ApiContact,
  values: CreateBillingAddressFormData,
): ContactBillingAddress | null {
  const addresses = contact.billing_addresses ?? [];
  if (addresses.length === 0) return null;

  const address = values.address.trim();
  const postalCode = values.postal_code.trim();
  // The (contact_id, billing_address_id) unique index means at most one entry
  // per contact matches a given address/postal, so this content match is
  // unambiguous when it hits.
  const exact = addresses.find(
    (billingAddress) =>
      billingAddress.address === address && billingAddress.postal_code === postalCode,
  );
  if (exact) return exact;

  // The create endpoint returns the full contact, not the created id, and the
  // server may normalise the stored address (so an exact match can miss). The
  // just-created join has the highest id, so fall back to it rather than
  // stranding the user. Reduce by id, not array position: the serializer orders
  // billing addresses default-first, so the newest is not necessarily last.
  return addresses.reduce((newest, candidate) =>
    candidate.id > newest.id ? candidate : newest,
  );
}

function FormFields({ error }: { error: Error | null }) {
  const { form } = useFormContext();
  const { t } = useTranslation();
  useServerErrors(error);
  const submitted = useStore(form.store, (state) => state.submissionAttempts > 0);

  const errorFor = (meta: { errors: unknown[] }, shouldShow: boolean): string | null => {
    if (!shouldShow) return null;
    const first = meta.errors[0];
    if (!first) return null;
    if (typeof first === 'string') return first;
    if (typeof first === 'object' && 'message' in first) {
      return String((first as { message: unknown }).message);
    }
    return String(first);
  };

  return (
    <div className="flex flex-col gap-8">
      <form.Field name="name">
        {(field) => (
          <Field>
            <FieldLabel htmlFor={NAME_FIELD_ID} className={LABEL_CLASS}>
              {t('create_billing_address.labels.name')}
            </FieldLabel>
            <FieldContent>
              <Input
                id={NAME_FIELD_ID}
                value={(field.state.value as string) ?? ''}
                onChange={(event) => field.handleChange(event.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('create_billing_address.placeholders.name')}
                autoComplete="off"
                className={INPUT_CLASS}
              />
            </FieldContent>
          </Field>
        )}
      </form.Field>

      <form.Field name="address">
        {(field) => {
          const fieldError = errorFor(
            field.state.meta,
            submitted || field.state.meta.isBlurred,
          );
          return (
            <Field>
              <FieldLabel htmlFor={ADDRESS_FIELD_ID} className={LABEL_CLASS}>
                {t('create_billing_address.labels.address')}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={ADDRESS_FIELD_ID}
                  value={(field.state.value as string) ?? ''}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_billing_address.placeholders.address')}
                  autoComplete="off"
                  className={INPUT_CLASS}
                />
                {fieldError && <FieldError>{fieldError}</FieldError>}
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      <form.Field name="postal_code">
        {(field) => {
          const value = (field.state.value as string) ?? '';
          const isInvalidPostalCode = value.length > 0 && !isBillingPostalCodeValid(value);
          const fieldError = errorFor(
            field.state.meta,
            submitted || field.state.meta.isBlurred || isInvalidPostalCode,
          );
          return (
            <Field>
              <FieldLabel htmlFor={POSTAL_CODE_FIELD_ID} className={LABEL_CLASS}>
                {t('create_billing_address.labels.postal_code')}
              </FieldLabel>
              <FieldContent>
                <Input
                  id={POSTAL_CODE_FIELD_ID}
                  value={value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                  inputMode="numeric"
                  placeholder={t('create_billing_address.placeholders.postal_code')}
                  autoComplete="off"
                  className={INPUT_CLASS}
                />
                {fieldError && <FieldError>{fieldError}</FieldError>}
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>
    </div>
  );
}

function FormFooter() {
  const { t } = useTranslation();

  return (
    <div className="-mx-8 border-t border-dinnerplate-white px-8 py-6">
      <div className="flex justify-end">
        <SubmitButton label={t('create_billing_address.actions.create')} />
      </div>
    </div>
  );
}

export function CreateBillingAddressForm({
  contactId,
  onSuccess,
  onPendingChange,
}: CreateBillingAddressFormProps) {
  const { create } = useContactBillingAddresses(contactId);
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const schema = useMemo(() => createBillingAddressFormSchema(t), [t]);

  useEffect(() => {
    onPendingChange?.(create.isPending);
    return () => onPendingChange?.(false);
  }, [create.isPending, onPendingChange]);

  const handleSubmit = useCallback(
    async (values: CreateBillingAddressFormData) => {
      setSubmitError(null);
      try {
        const contact = await create.mutateAsync({
          // Omit the label when blank so no empty string is stored (the billed
          // party then falls back to org/contact name).
          name: values.name?.trim() || undefined,
          address: values.address.trim(),
          postal_code: values.postal_code.trim(),
        });
        const billingAddress = findCreatedBillingAddress(contact, values);
        if (billingAddress) {
          onSuccess(contact, billingAddress);
        } else {
          // The address was created server-side but the response carried no
          // billing addresses to select (malformed/empty payload). Surface an
          // error instead of silently stranding the user on the form.
          toast({
            variant: 'destructive',
            description: t('create_billing_address.errors.lookup_failed'),
          });
        }
      } catch (err) {
        setSubmitError(err instanceof Error ? err : new Error('Failed to create billing address'));
      }
    },
    [create, onSuccess, t],
  );

  return (
    <Form<CreateBillingAddressFormData>
      schema={schema}
      defaultValues={{ name: '', address: '', postal_code: '' }}
      onSubmit={handleSubmit}
      isPending={create.isPending}
      className="flex flex-col gap-8 overflow-x-hidden px-8 pt-6 font-inter"
    >
      <FormFields error={submitError} />
      <FormFooter />
    </Form>
  );
}
